import { InjectableService } from '@/shared/services/InjectableService'
import type { AxiosResponse } from 'axios'
import { submitApiRequest, HttpMethod } from '@/services/aplosApi'
import type { MigrationConfig } from '../models/MigrationConfig'
import type { MigrationHistory } from '../models/MigrationHistory'

const BASE_PATH = '/v2/migration'

export interface MigrationHistoryListResponse {
  items: MigrationHistory[]
  count: number
  nextKey?: string
}

export interface ScheduleState {
  enabled: boolean
  scheduleExpression?: string
}

export interface IMigrationApiService {
  getConfig(migrationType: string): Promise<AxiosResponse<MigrationConfig>>
  updateConfig(migrationType: string, config: Partial<MigrationConfig>): Promise<AxiosResponse<MigrationConfig>>
  triggerRun(migrationType: string): Promise<AxiosResponse<unknown>>
  enableSchedule(migrationType: string): Promise<AxiosResponse<ScheduleState>>
  disableSchedule(migrationType: string): Promise<AxiosResponse<ScheduleState>>
  getScheduleState(migrationType: string): Promise<AxiosResponse<ScheduleState>>
  listHistory(migrationType: string, startKey?: string): Promise<AxiosResponse<MigrationHistoryListResponse>>
  resetCheckpoint(migrationType: string): Promise<AxiosResponse<unknown>>
}

export class MigrationApiService extends InjectableService implements IMigrationApiService {

  static readonly injectableKey = 'IMigrationApiService'

  get injectableKey() {
    return (this.constructor as typeof MigrationApiService).injectableKey
  }

  async getConfig(migrationType: string): Promise<AxiosResponse<MigrationConfig>> {
    return await submitApiRequest(HttpMethod.GET, `${BASE_PATH}/config/${migrationType}`)
  }

  async updateConfig(migrationType: string, config: Partial<MigrationConfig>): Promise<AxiosResponse<MigrationConfig>> {
    return await submitApiRequest(HttpMethod.PUT, `${BASE_PATH}/config/${migrationType}`, config)
  }

  async triggerRun(migrationType: string): Promise<AxiosResponse<unknown>> {
    return await submitApiRequest(HttpMethod.POST, `${BASE_PATH}/run/${migrationType}`)
  }

  async enableSchedule(migrationType: string): Promise<AxiosResponse<ScheduleState>> {
    return await submitApiRequest(HttpMethod.POST, `${BASE_PATH}/schedule/${migrationType}/enable`)
  }

  async disableSchedule(migrationType: string): Promise<AxiosResponse<ScheduleState>> {
    return await submitApiRequest(HttpMethod.POST, `${BASE_PATH}/schedule/${migrationType}/disable`)
  }

  async getScheduleState(migrationType: string): Promise<AxiosResponse<ScheduleState>> {
    return await submitApiRequest(HttpMethod.GET, `${BASE_PATH}/schedule/${migrationType}`)
  }

  async listHistory(migrationType: string, startKey?: string): Promise<AxiosResponse<MigrationHistoryListResponse>> {
    const qs = startKey ? `?start_key=${encodeURIComponent(startKey)}` : ''
    return await submitApiRequest(HttpMethod.GET, `${BASE_PATH}/history/${migrationType}${qs}`)
  }

  async resetCheckpoint(migrationType: string): Promise<AxiosResponse<unknown>> {
    return await submitApiRequest(HttpMethod.POST, `${BASE_PATH}/checkpoint/${migrationType}/reset`)
  }
}
